import React, { type HTMLAttributes } from 'react';
import type { RequestOptions } from 'searchkit';
import c from 'classnames';

import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import { Container, Text } from '@uncinc/uncinc-react-kitchen-sink';

import type {
  ParagraphHighlightedSelection as Type,
} from '../../../../types/uncinc_profile/paragraphs';
import { ButtonWrapper } from '../../../../components';
import MetaSearch from '../../../../components/Search/MetaSearch';
import {
  constructQuery,
  constructAdditionalSelectionFilters,
  constructSortingList,
} from '../../../../components/Search/MetaSearch/metaParagraphsHelpers';

import './style.scss';

interface ParagraphHighlightedSelectionProps
  extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableEntity<Type>;
}

const ParagraphHighlightedSelection: React.FC<ParagraphHighlightedSelectionProps> = ({
  id,
  entity,
}) => {
  const highlightedSelection = entity.toJS();
  const contentType = highlightedSelection.field_content_type[0].value;
  const selectionMode = highlightedSelection.field_selection_mode_highlighted[0].value;

  const title = highlightedSelection.field_title[0]?.value;
  const body = highlightedSelection.field_body[0]?.processed || '';

  const link = highlightedSelection.field_link[0];
  const linkTitle = link?.title;
  const linkUrl = link?.alias || link?.uri;

  const requestOptions: RequestOptions = {
    getBaseFilters: () => [
      constructQuery(contentType),
      constructAdditionalSelectionFilters(highlightedSelection),
    ],
  };

  let sortingList;
  if (selectionMode === 'manual') {
    // When the entities have been selected manually, we want to display
    // the results in the order they were selected in the CMS.
    // We do this by passing our own sortfunction through to the
    // SearchResults component in uncinc/react-searchkit.
    sortingList = constructSortingList(highlightedSelection);
  }

  const classNames = c(
    'Paragraph',
    'ParagraphMeta',
    'ParagraphHighlightedSelection',
    `ParagraphHighlightedSelection__${contentType}`,
  );

  return (
    <div id={id} className={classNames}>
      <Container>
        <div className="row">
          <div className="col-12">
            {(title || body) && (
              <div className="ParagraphHighlightedSelection__body">
                <Text
                  title={title}
                  text={body}
                />
              </div>
            )}
          </div>
          <div className="col-12">

            <MetaSearch
              requestOptions={requestOptions}
              contentType={contentType}
              sortingList={sortingList}
              // There's no limit in Drupal, however we should set a limit regardless.
              hitsPerPage={selectionMode === 'highlighted' ? 3 : 9}
            />

            {link && (
              <div className="ParagraphHighlightedSelection__links">
                {linkUrl && (
                  <ButtonWrapper
                    className="ParagraphHighlightedSelection__links__link"
                    href={linkUrl}
                  >
                    {linkTitle}
                  </ButtonWrapper>
                )}
              </div>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ParagraphHighlightedSelection;
